import * as Sentry from "@sentry/react";

import CONFIG from "Config";

/**
 * Host convention for the New UI Migration Switch (EAI-5651). The new Enflux UI
 * (platform-ui) is served from an `eai.new` prefix inside the school's host —
 * eai-api's per-environment `WEB_URL_NEW_UI`, `<school>.eai.new.<env>.enflux.com`.
 * This UI is served from the school host without that prefix: eai-api's
 * `WEB_URL` is `<school>.<env>.enflux.com`, though the `<school>.eai.<env>`
 * shape (this app's own config `domain`) resolves here too.
 *
 * This is the mirror image of platform-ui's
 * `src/features/auth/utils/legacy-ui-url.ts`, which keeps the school label and
 * drops the whole prefix through `new` to find its way back here. The two
 * halves have to agree on the convention, so changing it means changing both.
 */
const NEW_UI_HOST_LABEL = "new";

/** The label that marks a host as an Enflux UI zone in every environment. */
const UI_ZONE_HOST_LABEL = "eai";

/**
 * The DNS zone every Enflux-run UI host lives under. Only hosts inside it get
 * an origin derived for them — the convention says nothing about what exists
 * under anyone else's domain.
 */
const ENFLUX_ZONE_SUFFIX = ".enflux.com";

/**
 * Derives the new UI's origin from the host this UI is served from, for both
 * host shapes this UI answers on:
 *
 *  - `myschool.test.enflux.com` (eai-api's `WEB_URL`, the host sign-in links
 *    actually use) gets the whole `eai.new` prefix inserted after the school
 *    label, becoming `myschool.eai.new.test.enflux.com`.
 *  - `myschool.eai.test.enflux.com` (this app's config `domain`) already has
 *    the zone label, so only `new` is inserted after it — same destination.
 *
 * The school label is always kept — it is how both UIs identify a school, and
 * it is the key the `use_new_ui` flag itself is stored under.
 *
 * Returns null for any host the convention does not describe — localhost, IP
 * literals, anything outside the enflux.com zone — so an unrecognized host
 * yields no redirect rather than a guessed one.
 */
export function deriveNewUiOrigin(hostname: string): string | null {
    const host = hostname.trim().toLowerCase().replace(/\.$/, "");

    if (!host || host.startsWith("[") || /^\d+(?:\.\d+){3}$/.test(host)) {
        return null;
    }

    const labels = host.split(".");
    // Never invent a host out of something that is barely a domain.
    if (labels.length < 3) {
        return null;
    }

    // `lastIndexOf`, so a school literally named `eai` (`eai.eai.enflux.com`)
    // is measured from the zone label rather than from its own subdomain.
    const zoneIndex = labels.lastIndexOf(UI_ZONE_HOST_LABEL);
    if (zoneIndex >= 0) {
        // The zone label must have something after it to insert before;
        // `foo.eai` would otherwise become the meaningless `foo.eai.new`.
        if (zoneIndex >= labels.length - 1) {
            return null;
        }

        // Already a new-UI host: this UI is not served from one, but a
        // misconfigured DNS entry pointing here must not redirect to itself.
        if (labels[zoneIndex + 1] === NEW_UI_HOST_LABEL) {
            return null;
        }

        const newUiLabels = labels
            .slice(0, zoneIndex + 1)
            .concat(NEW_UI_HOST_LABEL, labels.slice(zoneIndex + 1));

        return `https://${newUiLabels.join(".")}`;
    }

    // No zone label: the WEB_URL shape, `<school>.<env zone>`. Only trusted
    // inside the Enflux zone — with no marker label in the host, the zone is
    // the only evidence the convention applies at all.
    if (!host.endsWith(ENFLUX_ZONE_SUFFIX)) {
        return null;
    }

    const newUiLabels = [labels[0], UI_ZONE_HOST_LABEL, NEW_UI_HOST_LABEL, ...labels.slice(1)];
    return `https://${newUiLabels.join(".")}`;
}

/**
 * Normalizes a configured new UI URL to a bare origin, tolerating the shapes a
 * config value realistically arrives in (`eai.new.enflux.com`, with a scheme,
 * with a trailing slash or path).
 *
 * Returns null when the value cannot be parsed into an http(s) origin — a
 * malformed setting must not turn into a redirect to nowhere.
 */
export function normalizeNewUiOrigin(configuredUrl: string): string | null {
    const value = (configuredUrl || "").trim();
    if (!value) {
        return null;
    }

    try {
        const url = new URL(/^[a-z][\w+.-]*:\/\//i.test(value) ? value : `https://${value}`);
        if (url.protocol !== "https:" && url.protocol !== "http:") {
            return null;
        }
        return url.origin;
    } catch (e) {
        return null;
    }
}

/**
 * Resolves where a school that *is* on the new UI should be sent.
 *
 * The `newUiUrl` config value wins when set, so an environment whose hosts do
 * not follow the `eai.new.*` convention — or a developer running platform-ui
 * locally — can name the new UI outright; otherwise the origin is derived from
 * the current host. Null means "no known new UI", and every caller must treat
 * that as "do not redirect": staying on this sign-in screen is always
 * recoverable, whereas sending a user to a wrong or empty origin locks them
 * out.
 */
export function resolveNewUiOrigin(
    hostname: string = window.location.hostname,
    configuredUrl: string = CONFIG.newUiUrl || ""
): string | null {
    return normalizeNewUiOrigin(configuredUrl) || deriveNewUiOrigin(hostname);
}

/**
 * Builds the new UI's sign-in URL, carrying over the context the user arrived
 * with so the hop is invisible to them: the email they typed and the deep link
 * they were originally headed for. Mirrors how `Login.handleNext` hands off to
 * another school's UI.
 */
export function buildNewUiLoginUrl(
    origin: string,
    context: { email?: string, redirect?: string | null } = {}
): string {
    const params: string[] = [];
    if (context.email) {
        params.push(`email=${encodeURIComponent(context.email)}`);
    }
    if (context.redirect) {
        params.push(`redirect=${encodeURIComponent(context.redirect)}`);
    }
    const query = params.join("&");
    return `${origin}/login${query ? `?${query}` : ""}`;
}

/**
 * Set once this tab has already bounced to the new UI. If the user comes back
 * anyway — a new UI that disagrees about the flag, a stale cache, a back
 * button — this stops the two sign-in screens from volleying the user between
 * them. Landing on a sign-in form is always recoverable; an infinite redirect
 * is not, and the toggle is exactly the kind of thing that is misconfigured at
 * 2am.
 */
export const NEW_UI_REDIRECT_STORAGE_KEY = "new_ui_redirect_attempted";

export function hasAlreadyRedirectedToNewUi(): boolean {
    try {
        return sessionStorage.getItem(NEW_UI_REDIRECT_STORAGE_KEY) === "true";
    } catch (e) {
        // Storage disabled (private mode, embedded webview). Treat as "not yet"
        // so the redirect still works for the common case.
        return false;
    }
}

export function markRedirectedToNewUi(): void {
    try {
        sessionStorage.setItem(NEW_UI_REDIRECT_STORAGE_KEY, "true");
    } catch (e) {
        // Non-critical: without the marker we lose only the loop guard.
    }
}

/**
 * New UI Migration Switch (EAI-5651): hands a school over to the new Enflux UI
 * when the sign-in lookup says `use_new_ui` is on for its subdomain.
 *
 * Every uncertain case deliberately declines to redirect, because the failure
 * modes are not symmetric — leaving a user on this sign-in screen costs them a
 * detour, while a wrong redirect can lock a school out of Enflux entirely:
 *
 *  - Anything other than an explicit `true` — an explicit `null` meaning
 *    redirection is turned off for the school (EAI-5649), the flag absent
 *    because the API predates it, a lookup that failed — reads as "stay put",
 *    never as "new UI".
 *    platform-ui's half of the switch makes the same choice with the inverse
 *    value, so a missing flag parks users wherever they already are instead of
 *    bouncing them between the two.
 *  - No resolvable new UI origin means there is nowhere safe to send them.
 *  - A second attempt in the same tab is suppressed by the loop guard above.
 *
 * Returns whether the browser is now navigating away, so the caller can stop
 * setting up a form the user will never see.
 */
export function redirectToNewUiIfNeeded(
    method: { use_new_ui?: boolean | null } | undefined,
    context: { email?: string, redirect?: string | null } = {}
): boolean {
    if (!method || method.use_new_ui !== true || hasAlreadyRedirectedToNewUi()) {
        return false;
    }

    const origin = resolveNewUiOrigin();
    if (!origin) {
        // Worth a trail: the flag is on, so someone expects this to work.
        Sentry.addBreadcrumb({
            message: "New UI redirect skipped: no new UI origin could be resolved",
            data: { hostname: window.location.hostname },
        });
        return false;
    }

    markRedirectedToNewUi();
    // `replace`, so the back button returns to wherever the user came from
    // rather than to a sign-in screen that immediately forwards them again.
    window.location.replace(buildNewUiLoginUrl(origin, context));
    return true;
}
